import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { shippingApi, locationsApi, organizationApi, productsApi, ordersApi } from '../services/api';
import { useToast } from './Toast';
import * as printerService from '../services/printer';
import { loadShippingDraft, removeShippingDraft, saveShippingDraft } from '../utils/shippingDraftStorage';
import {
  CheckIcon,
  ArrowPathIcon,
  PrinterIcon,
  ScaleIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
  StarIcon,
  CurrencyDollarIcon,
  GiftIcon,
  ArrowLeftIcon,
  ArrowsRightLeftIcon,
  TruckIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

// ============================================
// Types
// ============================================

interface OrderItem {
  id: string;
  title: string;
  sku?: string;
  variant_sku?: string;
  variant_title?: string;
  quantity: number;
  price: number;
  product_title?: string;
  image_url?: string;
  weight_oz?: number;
  variant_weight?: number;
  product_weight?: number;
  resolved_weight?: number;
}

interface OrderDetails {
  id: string;
  order_number: string;
  first_name?: string;
  last_name?: string;
  customer_email?: string;
  total_price: number;
  shipping_paid?: number;
  status: string;
  created_at: string;
  shipping_address_line1?: string;
  shipping_address_line2?: string;
  shipping_city?: string;
  shipping_state?: string;
  shipping_zip?: string;
  shipping_country?: string;
  shipping_phone?: string;
  shopify_customer_orders_count?: number | null;
  is_first_time_customer?: boolean | null;
  items?: OrderItem[];
  shipping_lines?: any[];
}

interface PackagePreset {
  id: string;
  name: string;
  length: number;
  width: number;
  height: number;
  weight?: number;
}

interface ShippingRate {
  rate_id: string;
  carrier_id?: string;
  carrier: string;
  carrier_code?: string;
  service: string;
  service_name?: string;
  service_code: string;
  total_cost: number;
  base_cost?: number;
  markup?: number;
  delivery_days?: number;
  delivery_date?: string;
  negotiated?: boolean;
  is_third_party?: boolean;
  third_party_account?: {
    nickname: string;
    account_number: string;
  };
  boxes?: number[];
  num_packages?: number;
}

interface Location {
  id: string;
  name: string;
  company_name: string;
  address_line1: string;
  city: string;
  state: string;
  zip: string;
  is_default: boolean;
}

interface BoxItem {
  id: string;
  title: string;
  sku?: string;
  variant_title?: string;
  variant_sku?: string;
  product_variant_id?: string;
  quantity: number;
  weight_oz?: number;
  variant_weight?: number;
  product_weight?: number;
  resolved_weight?: number;
  boxId: string; // Which box this item is assigned to
}

interface Box {
  id: string;
  weightLb: number;
  weightOz: number;
  length: number;
  width: number;
  height: number;
  presetId: string;
  packagingWeight?: number; // oz - weight of the empty box/packaging itself
}

interface MethodMatch {
  original_method?: string;
  suggested_rate_index?: number;
  confidence?: string;
  message?: string;
  requires_free_shipping?: boolean;
}

interface UnifiedShippingViewProps {
  order: OrderDetails;
  onLabelCreated: () => void;
  onClose?: () => void;
  skipFulfillment?: boolean;
}

// ============================================
// Carrier Badge
// ============================================

const CarrierBadge: React.FC<{ carrier: string }> = ({ carrier }) => {
  const carrierLower = carrier?.toLowerCase() || '';
  
  if (carrierLower.includes('ups')) {
    return <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded-lg">UPS</span>;
  } else if (carrierLower.includes('usps')) {
    return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded-lg">USPS</span>;
  } else if (carrierLower.includes('fedex')) {
    return <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-bold rounded-lg">FedEx</span>;
  } else if (carrierLower.includes('dhl')) {
    return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-lg">DHL</span>;
  }
  return <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-bold rounded-lg">{carrier?.substring(0, 4).toUpperCase()}</span>;
};

// ============================================
// Split Modal Component
// ============================================

interface SplitModalProps {
  item: BoxItem;
  boxes: Box[];
  onSplit: (splits: { boxId: string; quantity: number }[], newBoxQuantity?: number) => void;
  onCancel: () => void;
}

const SplitModal: React.FC<SplitModalProps> = ({ item, boxes, onSplit, onCancel }) => {
  // Start with 0 assigned to all boxes - user assigns where needed
  const [splits, setSplits] = useState<{ boxId: string; quantity: number }[]>(
    boxes.map(b => ({ boxId: b.id, quantity: 0 }))
  );
  const [newBoxQuantity, setNewBoxQuantity] = useState(0);

  const totalAssigned = splits.reduce((sum, s) => sum + s.quantity, 0) + newBoxQuantity;
  const remaining = item.quantity - totalAssigned;

  const handleSubmit = () => {
    const validSplits = splits.filter(s => s.quantity > 0);
    onSplit(validSplits, newBoxQuantity > 0 ? newBoxQuantity : undefined);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-96 max-h-[80vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-2">Split Item Across Boxes</h3>
        <p className="text-sm text-slate-600 mb-1">{item.title}</p>
        <p className="text-sm text-slate-500 mb-4">
          Assign <span className="font-bold text-slate-800">{item.quantity}</span> items to boxes
        </p>

        {/* Status Banner */}
        <div className={`text-center py-2 rounded-lg mb-4 ${
          remaining === 0 ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'
        }`}>
          {remaining === 0 ? (
            <span className="font-medium">✓ All {item.quantity} assigned</span>
          ) : (
            <span className="font-medium">{remaining} left to assign</span>
          )}
        </div>
        
        {/* Existing Boxes */}
        <div className="space-y-2 mb-4">
          {boxes.map((box, idx) => (
            <div key={box.id} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
              <span className="text-sm font-medium text-slate-700">Box {idx + 1}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSplits(prev => prev.map(s => 
                    s.boxId === box.id ? { ...s, quantity: Math.max(0, s.quantity - 1) } : s
                  ))}
                  disabled={splits.find(s => s.boxId === box.id)?.quantity === 0}
                  className="w-8 h-8 rounded-lg bg-white border border-slate-300 hover:bg-slate-100 text-lg font-medium disabled:opacity-30 disabled:cursor-not-allowed"
                >−</button>
                <span className="w-10 text-center text-lg font-semibold">
                  {splits.find(s => s.boxId === box.id)?.quantity || 0}
                </span>
                <button
                  onClick={() => setSplits(prev => prev.map(s => 
                    s.boxId === box.id && remaining > 0
                      ? { ...s, quantity: s.quantity + 1 } : s
                  ))}
                  disabled={remaining === 0}
                  className="w-8 h-8 rounded-lg bg-white border border-slate-300 hover:bg-slate-100 text-lg font-medium disabled:opacity-30 disabled:cursor-not-allowed"
                >+</button>
              </div>
            </div>
          ))}
        </div>

        {/* New Box Section */}
        <div className="border-t border-slate-200 pt-4 mb-4">
          <div className="flex items-center justify-between bg-blue-50 rounded-lg p-3 border border-blue-200">
            <div>
              <span className="text-sm font-medium text-blue-700">+ New Box</span>
              <p className="text-xs text-blue-600">Creates Box {boxes.length + 1}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setNewBoxQuantity(Math.max(0, newBoxQuantity - 1))}
                disabled={newBoxQuantity === 0}
                className="w-8 h-8 rounded-lg bg-white border border-blue-300 hover:bg-blue-100 text-lg font-medium disabled:opacity-30 disabled:cursor-not-allowed"
              >−</button>
              <span className="w-10 text-center text-lg font-semibold text-blue-700">
                {newBoxQuantity}
              </span>
              <button
                onClick={() => remaining > 0 && setNewBoxQuantity(newBoxQuantity + 1)}
                disabled={remaining === 0}
                className="w-8 h-8 rounded-lg bg-white border border-blue-300 hover:bg-blue-100 text-lg font-medium disabled:opacity-30 disabled:cursor-not-allowed"
              >+</button>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button 
            onClick={onCancel} 
            className="flex-1 px-4 py-2.5 text-sm font-medium border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={totalAssigned === 0 || remaining > 0}
            className="flex-1 px-4 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Apply Split
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Confirmation Modal
// ============================================

interface ConfirmModalProps {
  rate: ShippingRate;
  boxCount: number;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({ rate, boxCount, onConfirm, onCancel, isLoading }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl shadow-xl p-6 w-96">
      <h3 className="text-lg font-semibold mb-4">Create Shipping Labels?</h3>
      
      <div className="bg-slate-50 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-3 mb-2">
          <CarrierBadge carrier={rate.carrier} />
          <div>
            <p className="font-semibold text-slate-800">{rate.service_name || rate.service}</p>
            <p className="text-sm text-slate-500">{rate.delivery_days ? `${rate.delivery_days} business days` : 'Delivery time varies'}</p>
          </div>
        </div>
        <div className="flex justify-between items-center pt-3 border-t border-slate-200">
          <span className="text-slate-600">{boxCount} {boxCount === 1 ? 'label' : 'labels'}</span>
          <span className="text-xl font-bold text-slate-900">${(rate.total_cost || 0).toFixed(2)}</span>
        </div>
      </div>

      <div className="flex gap-3">
        <button 
          onClick={onCancel} 
          disabled={isLoading}
          className="flex-1 px-4 py-2.5 text-sm font-medium border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isLoading}
          className="flex-1 px-4 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <><ArrowPathIcon className="w-4 h-4 animate-spin" /> Creating...</>
          ) : (
            <><PrinterIcon className="w-4 h-4" /> Create Labels</>
          )}
        </button>
      </div>
    </div>
  </div>
);

// ============================================
// Main Component
// ============================================

export const UnifiedShippingView: React.FC<UnifiedShippingViewProps> = ({
  order,
  onLabelCreated,
  skipFulfillment = false,
}) => {
  const toast = useToast();
  const customerOrderCountRaw = (order as any).shopify_customer_orders_count;
  const customerOrderCount = customerOrderCountRaw !== undefined && customerOrderCountRaw !== null
    ? parseInt(String(customerOrderCountRaw), 10)
    : null;
  const isFirstTimeCustomer = (order as any).is_first_time_customer === true || customerOrderCount === 1;
  
  // Initialize items with box assignments
  const initializeItems = useCallback((): BoxItem[] => {
    const items = order.items || [];
    console.log('[UnifiedShipping] Incoming order items:', items.map((i: any) => ({
      id: i.id,
      title: i.title,
      sku: (i as any).variant_sku || i.sku,
      product_variant_id: (i as any).product_variant_id,
      variant_weight: (i as any).variant_weight,
      product_weight: (i as any).product_weight,
      resolved_weight: (i as any).resolved_weight,
      weight_oz: i.weight_oz,
    })));
    return items.map(item => ({
      id: item.id,
      title: item.title || item.product_title || 'Unknown Item',
      sku: (item as any).variant_sku || item.sku,
      variant_title: (item as any).variant_title,
      product_variant_id: (item as any).product_variant_id,
      quantity: item.quantity,
      resolved_weight: (() => {
        const raw = (item as any).resolved_weight ?? (item as any).variant_weight ?? (item as any).product_weight ?? item.weight_oz ?? 0;
        return parseFloat(String(raw)) || 0;
      })(),
      weight_oz: (() => {
        const raw = (item as any).resolved_weight ?? (item as any).variant_weight ?? (item as any).product_weight ?? item.weight_oz ?? 0;
        return parseFloat(String(raw)) || 0;
      })(),
      variant_weight: (item as any).variant_weight,
      product_weight: (item as any).product_weight,
      boxId: '1', // Default all items to box 1
    }));
  }, [order.items]);

  // Calculate total weight for a box based on assigned items
  // packagingWeight: weight of the empty box in oz (from preset), defaults to 0
  const calculateBoxWeight = useCallback((boxId: string, items: BoxItem[], packagingWeight: number = 0): { lb: number; oz: number } => {
    let totalOz = packagingWeight;
    const boxItems = items.filter(i => i.boxId === boxId);
    const weightBreakdown: Array<{title: string; qty: number; unitWeight: number; totalWeight: number}> = [];
    
    boxItems.forEach(item => {
      const weight = parseFloat(String(
        item.resolved_weight ??
        item.variant_weight ??
        item.product_weight ??
        item.weight_oz ??
        0
      )) || 0;
      const itemTotal = weight * item.quantity;
      totalOz += itemTotal;
      weightBreakdown.push({
        title: item.title?.substring(0, 30) || 'Unknown',
        qty: item.quantity,
        unitWeight: weight,
        totalWeight: itemTotal,
      });
    });
    
    // Log detailed weight breakdown for debugging
    console.log(`[UnifiedShipping] Box ${boxId} weight breakdown:`, {
      packaging: packagingWeight,
      items: weightBreakdown,
      totalOz,
      totalLb: (totalOz / 16).toFixed(2),
    });
    
    return {
      lb: Math.floor(totalOz / 16),
      oz: Math.round(totalOz % 16),
    };
  }, []);

  // Draft persistence
  const draftKey = `unified_shipping_draft_${order.id}`;
  const loadDraft = () => {
    // For reship mode (skipFulfillment=true), always use fresh data - clear any stale draft
    if (skipFulfillment) {
      removeShippingDraft(draftKey);
      console.log('[UnifiedShipping] Cleared draft for reship mode - using fresh data');
      return null;
    }
    return loadShippingDraft(draftKey);
  };
  const draft = loadDraft();
  const [didClearEmptyOrder, setDidClearEmptyOrder] = useState(false);
  const [didFetchEmptyOrder, setDidFetchEmptyOrder] = useState(false);

  // If we somehow got an empty order payload (e.g., stale draft with no items), clear the draft once and allow fresh load.
  useEffect(() => {
    if (didClearEmptyOrder) return;
    const hasItems = (order.items || []).length > 0;
    if (!hasItems && draft) {
      try {
      removeShippingDraft(draftKey);
        console.warn('[UnifiedShipping] Cleared stale draft because order items were empty');
      } catch {
        /* ignore */
      }
      setDidClearEmptyOrder(true);
    }
  }, [order.items, draft, draftKey, didClearEmptyOrder]);

  // If order.items is empty, fetch fresh order data to hydrate items (covers shipped orders/reships that loaded with stale drafts).
  useEffect(() => {
    const hasItems = (order.items || []).length > 0;
    if (hasItems || didFetchEmptyOrder) return;
    if (!order?.id) return;

    const fetchItems = async () => {
      try {
        const result = await ordersApi.get(order.id);
        const fetched = result?.items || [];
        if (fetched.length > 0) {
          const mapped: BoxItem[] = fetched.map((item: any) => ({
            id: item.id,
            title: item.title || item.product_title || 'Unknown Item',
            sku: item.variant_sku || item.sku,
            variant_title: item.variant_title,
            product_variant_id: item.product_variant_id,
            quantity: item.quantity || item.unfulfilled_quantity || 1,
            resolved_weight: (() => {
              const raw = item.resolved_weight ?? item.variant_weight ?? item.product_weight ?? item.weight_oz ?? 0;
              return parseFloat(String(raw)) || 0;
            })(),
            weight_oz: (() => {
              const raw = item.resolved_weight ?? item.variant_weight ?? item.product_weight ?? item.weight_oz ?? 0;
              return parseFloat(String(raw)) || 0;
            })(),
            variant_weight: item.variant_weight,
            product_weight: item.product_weight,
            boxId: '1',
          }));

          const weight = calculateBoxWeight('1', mapped, 0);
          setItems(mapped);
          setBoxes([{
            id: '1',
            weightLb: weight.lb,
            weightOz: weight.oz,
            length: 12,
            width: 10,
            height: 6,
            presetId: '',
            packagingWeight: 0,
          }]);
          try {
          removeShippingDraft(draftKey);
          } catch {
            /* ignore */
          }
          setDidResetEmptyDraft(true);
          console.log('[UnifiedShipping] Hydrated items from fresh order fetch');
        }
      } catch (error) {
        console.warn('[UnifiedShipping] Failed to hydrate empty order items', error);
      } finally {
        setDidFetchEmptyOrder(true);
      }
    };

    fetchItems();
  }, [order?.id, order.items, didFetchEmptyOrder, calculateBoxWeight, draftKey]);

  // State - restore from draft if available
  const [step, setStep] = useState<1 | 2>(() => draft?.step || 1);
  const [items, setItems] = useState<BoxItem[]>(() => {
    if (draft?.items && draft.items.length > 0) {
      console.log('[UnifiedShipping] Restoring items from draft:', draft.items.length, 'items');
      const freshItems = initializeItems();
      
      // Use draft items directly but update weight data from fresh items
      const restoredItems = draft.items.map((draftItem: BoxItem) => {
        // For split items, find the original item to get weight
        const originalId = draftItem.id.includes('-split-') 
          ? draftItem.id.split('-split-')[0] 
          : draftItem.id;
        const freshItem = freshItems.find(f => f.id === originalId);
        
        if (freshItem) {
          const freshResolved =
            freshItem.resolved_weight ??
            freshItem.variant_weight ??
            freshItem.product_weight ??
            freshItem.weight_oz ??
            0;
          // Update with fresh weight data but keep draft's boxId and quantity
          return { 
            ...draftItem, 
            resolved_weight: parseFloat(String(freshResolved)) || 0,
            variant_weight: freshItem.variant_weight,
            product_weight: freshItem.product_weight,
            weight_oz: freshItem.weight_oz,
            product_variant_id: freshItem.product_variant_id,
            boxId: draftItem.boxId || '1',
          };
        }
        return { ...draftItem, boxId: draftItem.boxId || '1' };
      });
      
      console.log('[UnifiedShipping] Restored items:', restoredItems.map((i: BoxItem) => ({ id: i.id.substring(0, 8), boxId: i.boxId, qty: i.quantity })));
      return restoredItems;
    }
    console.log('[UnifiedShipping] No draft items, initializing fresh');
    return initializeItems();
  });
  const [boxes, setBoxes] = useState<Box[]>(() => {
    if (draft?.boxes) {
      console.log('[UnifiedShipping] Restoring boxes from draft:', draft.boxes.map((b: Box) => b.id));
      return draft.boxes;
    }
    console.log('[UnifiedShipping] No draft boxes, creating default box');
    const initialWeight = calculateBoxWeight('1', initializeItems(), 0);
    return [{
      id: '1',
      weightLb: initialWeight.lb,
      weightOz: initialWeight.oz,
      length: 12,
      width: 10,
      height: 6,
      presetId: '',
      packagingWeight: 0,
    }];
  });
  const [packagePresets, setPackagePresets] = useState<PackagePreset[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>(() => draft?.selectedLocationId || '');
  const [selectedRate, setSelectedRate] = useState<ShippingRate | null>(() => draft?.selectedRate || null);
  const [availableRates, setAvailableRates] = useState<ShippingRate[]>(() => draft?.availableRates || []);
  const [signatureOption, setSignatureOption] = useState<'none' | 'signature' | 'adult_signature'>(() => draft?.signatureOption || 'none');
  const [insuranceOption, setInsuranceOption] = useState<'none' | 'carrier' | 'parcelguard'>(() => draft?.insuranceOption || 'none');
  const [insuranceAmount, setInsuranceAmount] = useState(() => draft?.insuranceAmount || 0);
  const [methodMatch, setMethodMatch] = useState<MethodMatch | null>(() => draft?.methodMatch || null);
  const [maxPackageWeightLbs, setMaxPackageWeightLbs] = useState<number | undefined>(undefined);
  
  // UI State
  const [loadingRates, setLoadingRates] = useState(false);
  const [creatingLabel, setCreatingLabel] = useState(false);
  const [ratesError, setRatesError] = useState<string | null>(null);
  const [maxWeightError, setMaxWeightError] = useState<string | null>(null);
  const [splittingItem, setSplittingItem] = useState<BoxItem | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [createdLabels, setCreatedLabels] = useState<any[]>([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [didResetEmptyDraft, setDidResetEmptyDraft] = useState(false);

  // Warn user if they try to close/refresh with unconfirmed labels
  const hasUnconfirmedLabels = createdLabels.length > 0 && showReviewModal;
  useEffect(() => {
    if (!hasUnconfirmedLabels) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnconfirmedLabels]);

  // ============================================
  // Draft Persistence
  // ============================================

  // Auto-save draft when state changes
  useEffect(() => {
    const draftData = {
      step,
      items,
      boxes,
      selectedLocationId,
      selectedRate,
      availableRates,
      signatureOption,
      insuranceOption,
      insuranceAmount,
      methodMatch,
    };
    saveShippingDraft(draftKey, draftData);
  }, [step, items, boxes, selectedLocationId, selectedRate, availableRates, signatureOption, insuranceOption, insuranceAmount, methodMatch, draftKey]);

  // Clear draft when label is created successfully
  const clearDraft = () => {
    removeShippingDraft(draftKey);
  };

  // ============================================
  // Initialize
  // ============================================

  useEffect(() => {
    const loadData = async () => {
      try {
        const [presetsResult, locationsResult, orgSettings] = await Promise.all([
          shippingApi.getPackagePresets(),
          locationsApi.list(),
          organizationApi.getSettings(),
        ]);
        
        setPackagePresets(presetsResult.presets || []);
        setLocations(locationsResult.locations || []);
        if (orgSettings?.max_package_weight_lbs !== undefined) {
          setMaxPackageWeightLbs(orgSettings.max_package_weight_lbs);
        }
        
        const defaultLoc = locationsResult.locations?.find((l: Location) => l.is_default);
        if (defaultLoc) {
          setSelectedLocationId(defaultLoc.id);
        } else if (locationsResult.locations?.length > 0) {
          setSelectedLocationId(locationsResult.locations[0].id);
        }
      } catch (error) {
        console.error('Failed to load initial data:', error);
      }
    };
    loadData();
  }, []);

  // ============================================
  // Auto-recalculate box weights when items change
  // ============================================

  useEffect(() => {
    setBoxes(prevBoxes => {
      console.log('[UnifiedShipping] Recalculating weights for boxes:', prevBoxes.map(b => b.id));
      return prevBoxes.map(box => {
        const newWeight = calculateBoxWeight(box.id, items, box.packagingWeight ?? 0);
        return {
          ...box,
          weightLb: newWeight.lb,
          weightOz: newWeight.oz,
        };
      });
    });
  }, [items, calculateBoxWeight]);

  // Try to auto-refresh missing weights from Shopify (by variant id) or by SKU lookup.
  // Uses a ref for the in-flight guard to avoid stale-closure races, and processes
  // items sequentially to prevent request floods that trip the rate limiter.
  const refreshingRef = useRef<Set<string>>(new Set());
  const refreshRanRef = useRef(false);
  useEffect(() => {
    if (refreshRanRef.current) return;
    const missing = items.filter(i => (i.resolved_weight ?? 0) <= 0);
    if (!missing.length) return;
    refreshRanRef.current = true;

    const resolveWeights = async () => {
      for (const item of missing) {
        const key = item.product_variant_id || (item.sku ? `sku:${item.sku}` : null);
        if (!key) continue;
        if (refreshingRef.current.has(key)) continue;

        refreshingRef.current.add(key);
        try {
          // First try by variant id if available
          if (item.product_variant_id) {
            const result = await productsApi.refreshVariantWeight(item.product_variant_id);
            const weight = result?.weight ?? 0;
            if (weight > 0) {
              setItems(prev => prev.map(i =>
                i.product_variant_id === item.product_variant_id
                  ? { ...i, resolved_weight: weight, variant_weight: weight, weight_oz: weight }
                  : i
              ));
              continue;
            }
          }

          // Fallback by SKU search to locate variant and weight
          if (item.sku) {
            try {
              const searchResult = await productsApi.search(item.sku);
              const products = searchResult?.products || [];
              let foundWeight = 0;
              let foundVariantId: string | undefined;

              for (const p of products) {
                if (p.sku === item.sku && p.weight) {
                  foundWeight = parseFloat(String(p.weight)) || 0;
                }
                const variants = p.variants || p.product_variants || [];
                const match = variants.find((v: any) => v.sku === item.sku);
                if (match) {
                  foundVariantId = match.id;
                  if (match.weight) {
                    foundWeight = parseFloat(String(match.weight)) || foundWeight;
                  }
                  break;
                }
              }

              if (foundWeight > 0 || foundVariantId) {
                setItems(prev => prev.map(i =>
                  i.id === item.id
                    ? {
                        ...i,
                        product_variant_id: i.product_variant_id || foundVariantId,
                        resolved_weight: foundWeight || i.resolved_weight || 0,
                        variant_weight: foundWeight || i.variant_weight,
                        weight_oz: foundWeight || i.weight_oz,
                      }
                    : i
                ));
              }
            } catch (err) {
              console.warn('[UnifiedShipping] SKU lookup failed', item.sku, err);
            }
          }
        } catch (error) {
          console.warn('[UnifiedShipping] Failed to refresh weight', key, error);
        } finally {
          refreshingRef.current.delete(key);
        }
      }
    };
    resolveWeights();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clear max weight error when boxes are within limit
  useEffect(() => {
    if (!maxPackageWeightLbs) {
      setMaxWeightError(null);
      return;
    }
    const maxOz = maxPackageWeightLbs * 16;
    const overLimit = boxes.some(b => (b.weightLb * 16 + b.weightOz) > maxOz);
    if (!overLimit) {
      setMaxWeightError(null);
    }
  }, [boxes, maxPackageWeightLbs]);

  // ============================================
  // Item Assignment
  // ============================================

  const assignItemToBox = (itemId: string, newBoxId: string) => {
    setItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, boxId: newBoxId } : item
    ));
  };

  const getOriginalItemId = (id: string) => (
    id.includes('-split-') ? id.split('-split-')[0] : id
  );

  const getNextBoxId = (existingBoxes: Box[]): string => {
    const maxNumericId = existingBoxes.reduce((max, box) => {
      const numeric = parseInt(String(box.id), 10);
      return Number.isFinite(numeric) ? Math.max(max, numeric) : max;
    }, 0);
    return String(maxNumericId + 1);
  };

  const createDefaultBox = (id: string, template?: Box): Box => ({
    id,
    weightLb: 0,
    weightOz: 0,
    length: template?.length ?? 12,
    width: template?.width ?? 10,
    height: template?.height ?? 6,
    presetId: template?.presetId ?? '',
    packagingWeight: template?.packagingWeight ?? 0,
  });

  const handleSplit = (itemId: string, splits: { boxId: string; quantity: number }[], newBoxQuantity?: number) => {
    const originalItem = items.find(i => i.id === itemId);
    if (!originalItem) return;

    // Create new box if needed, copying dims from the first box
    let newBoxId: string | null = null;
    if (newBoxQuantity && newBoxQuantity > 0) {
      newBoxId = getNextBoxId(boxes);
      const templateBox = boxes[0];
      setBoxes(prev => [...prev, createDefaultBox(newBoxId!, templateBox)]);
    }

    // Remove original item and add split items
    setItems(prev => {
      const filtered = prev.filter(i => i.id !== itemId);
      
      // Items for existing boxes
      const existingBoxItems = splits.map((split, idx) => ({
        ...originalItem,
        id: `${itemId}-split-${idx}`,
        quantity: split.quantity,
        boxId: split.boxId,
      }));
      
      // Item for new box if created
      const newBoxItems = newBoxId && newBoxQuantity ? [{
        ...originalItem,
        id: `${itemId}-split-new`,
        quantity: newBoxQuantity,
        boxId: newBoxId,
      }] : [];
      
      return [...filtered, ...existingBoxItems, ...newBoxItems];
    });
    setSplittingItem(null);
  };

  const splitItemOnePerBox = (itemId: string) => {
    const originalItem = items.find(i => i.id === itemId);
    if (!originalItem || originalItem.quantity <= 1) return;

    const totalUnits = originalItem.quantity;
    const existingBoxIds = boxes.map(b => b.id);
    const neededNewBoxes = Math.max(0, totalUnits - existingBoxIds.length);
    const templateBox = boxes[0];

    const newBoxIds: string[] = [];
    if (neededNewBoxes > 0) {
      let workingBoxes = [...boxes];
      for (let i = 0; i < neededNewBoxes; i++) {
        const nextId = getNextBoxId(workingBoxes);
        newBoxIds.push(nextId);
        workingBoxes.push(createDefaultBox(nextId, templateBox));
      }
      setBoxes(prev => [...prev, ...newBoxIds.map(id => createDefaultBox(id, templateBox))]);
    }

    const targetBoxIds = [...existingBoxIds, ...newBoxIds].slice(0, totalUnits);
    setItems(prev => {
      const filtered = prev.filter(i => i.id !== itemId);
      const splitItems = targetBoxIds.map((boxId, idx) => ({
        ...originalItem,
        id: `${itemId}-split-${idx}-${boxId}`,
        quantity: 1,
        boxId,
      }));
      return [...filtered, ...splitItems];
    });
    setSplittingItem(null);
  };

  const clearSplitGroup = (splitItemId: string) => {
    const originalId = getOriginalItemId(splitItemId);
    const splitGroup = items.filter(i => getOriginalItemId(i.id) === originalId);
    if (!splitGroup.length) return;

    const exemplar = splitGroup[0];
    const mergedQty = splitGroup.reduce((sum, i) => sum + (i.quantity || 0), 0);
    const fallbackBoxId = boxes[0]?.id || '1';
    const preferredBoxId = splitGroup.find(i => boxes.some(b => b.id === i.boxId))?.boxId || fallbackBoxId;

    // Determine which boxes will be empty after merging (exclude the preferred box)
    const splitBoxIds = new Set(splitGroup.map(i => i.boxId));
    const otherItems = items.filter(i => getOriginalItemId(i.id) !== originalId);
    const boxesWithOtherItems = new Set(otherItems.map(i => i.boxId));

    setItems(prev => {
      const withoutGroup = prev.filter(i => getOriginalItemId(i.id) !== originalId);
      return [
        ...withoutGroup,
        {
          ...exemplar,
          id: originalId,
          quantity: mergedQty,
          boxId: preferredBoxId,
        },
      ];
    });

    // Remove boxes that will be empty after the merge (keep at least one)
    setBoxes(prev => {
      const toRemove = prev.filter(b =>
        b.id !== preferredBoxId &&
        splitBoxIds.has(b.id) &&
        !boxesWithOtherItems.has(b.id)
      );
      if (toRemove.length === 0) return prev;
      const remaining = prev.filter(b => !toRemove.some(r => r.id === b.id));
      return remaining.length > 0 ? remaining : [prev[0]];
    });

    // Reset rate selection because package composition changed.
    setAvailableRates([]);
    setSelectedRate(null);
    setMethodMatch(null);
    setStep(1);
    setRatesError(null);
  };

  // ============================================
  // Box Management
  // ============================================

  const addBox = () => {
    const newId = getNextBoxId(boxes);
    setBoxes(prev => [...prev, createDefaultBox(newId)]); // Add to end (numbers stay consistent)
  };

  const removeBox = (boxId: string) => {
    if (boxes.length <= 1) return;

    const remainingBoxes = boxes.filter(b => b.id !== boxId);
    const fallbackBoxId = remainingBoxes[0]?.id;
    if (!fallbackBoxId) return;

    // Move items from removed box to the first remaining valid box
    setItems(prev => prev.map(item =>
      item.boxId === boxId ? { ...item, boxId: fallbackBoxId } : item
    ));

    setBoxes(remainingBoxes);
  };

  const updateBox = (boxId: string, updates: Partial<Box>) => {
    setBoxes(prev => prev.map(b => 
      b.id === boxId ? { ...b, ...updates } : b
    ));
  };

  const applyPresetToBox = (boxId: string, preset: PackagePreset) => {
    const packagingWeight = preset.weight || 0;
    const newWeight = calculateBoxWeight(boxId, items, packagingWeight);
    updateBox(boxId, {
      presetId: preset.id,
      length: preset.length,
      width: preset.width,
      height: preset.height,
      packagingWeight,
      weightLb: newWeight.lb,
      weightOz: newWeight.oz,
    });
  };

  // ============================================
  // Computed Values
  // ============================================

  const itemsByBox = useMemo(() => {
    console.log('[UnifiedShipping] Computing itemsByBox - boxes:', boxes.map(b => b.id), 'items:', items.map(i => ({ id: i.id.substring(0, 8), boxId: i.boxId })));
    const grouped: Record<string, BoxItem[]> = {};
    boxes.forEach(box => {
      grouped[box.id] = items.filter(i => i.boxId === box.id);
    });
    return grouped;
  }, [items, boxes]);

  // If a saved draft has empty boxes but items exist (e.g., reship), rebuild a default box and assign all items to box 1.
  useEffect(() => {
    if (didResetEmptyDraft) return;
    const hasItems = items.length > 0;
    const allBoxesEmpty = boxes.length > 0 && boxes.every(b => (itemsByBox[b.id] || []).length === 0);
    const validBoxIds = new Set(boxes.map(b => b.id));
    const hasOrphanedAssignments = items.some(i => !validBoxIds.has(i.boxId));
    if (hasOrphanedAssignments && boxes.length > 0) {
      const fallbackBoxId = boxes[0].id;
      setItems(prev => prev.map(i => validBoxIds.has(i.boxId) ? i : { ...i, boxId: fallbackBoxId }));
      setAvailableRates([]);
      setSelectedRate(null);
      setStep(1);
      setRatesError('Some items were assigned to removed boxes and were moved to Box 1.');
      return;
    }

    if (hasItems && allBoxesEmpty) {
      // Clear stale draft so we don't persist an empty state
      try {
      removeShippingDraft(draftKey);
      } catch {
        /* ignore */
      }
      const reassignedItems = items.map(i => ({ ...i, boxId: '1' }));
      const weight = calculateBoxWeight('1', reassignedItems, 0);
      setItems(reassignedItems);
      setBoxes([{
        id: '1',
        weightLb: weight.lb,
        weightOz: weight.oz,
        length: 12,
        width: 10,
        height: 6,
        presetId: '',
        packagingWeight: 0,
      }]);
      setDidResetEmptyDraft(true);
      console.log('[UnifiedShipping] Draft had empty boxes; rebuilt default box with all items');
    }
  }, [items, boxes, itemsByBox, calculateBoxWeight, didResetEmptyDraft, draftKey]);

  const totalWeight = useMemo(() => {
    let oz = 0;
    boxes.forEach(box => {
      oz += box.weightLb * 16 + box.weightOz;
    });
    const lb = Math.floor(oz / 16);
    oz = oz % 16;
    return { lb, oz };
  }, [boxes]);

  const allItemsAssigned = useMemo(() => {
    return items.every(item => item.boxId && boxes.some(b => b.id === item.boxId));
  }, [items, boxes]);

  const sortedRates = useMemo(() => {
    return [...availableRates].sort((a, b) => a.total_cost - b.total_cost);
  }, [availableRates]);

  const cheapestRate = sortedRates[0];
  const suggestedRate = methodMatch?.suggested_rate_index !== null && methodMatch?.suggested_rate_index !== undefined
    ? availableRates[methodMatch.suggested_rate_index]
    : null;

  // ============================================
  // Fetch Rates
  // ============================================

  const formatLbOz = (totalOz: number) => {
    const lb = Math.floor(totalOz / 16);
    const oz = totalOz % 16;
    return `${lb} lb ${oz} oz`;
  };

  const validateMaxWeight = useCallback((context: 'rates' | 'labels') => {
    if (!maxPackageWeightLbs) return true;
    const maxOz = maxPackageWeightLbs * 16;
    const overIndex = boxes.findIndex(b => (b.weightLb * 16 + b.weightOz) > maxOz);
    if (overIndex >= 0) {
      const overBox = boxes[overIndex];
      const msg = `Box ${overIndex + 1} is over the max package weight of ${maxPackageWeightLbs} lb (${formatLbOz(overBox.weightLb * 16 + overBox.weightOz)}).`;
      setMaxWeightError(msg);
      if (context === 'rates') {
        setRatesError(msg);
      } else {
        toast.error(msg);
      }
      return false;
    }
    setMaxWeightError(null);
    return true;
  }, [boxes, maxPackageWeightLbs, toast]);

  const fetchRates = useCallback(async () => {
    if (!order?.id) return;
    
    setRatesError(null);
    
    if (!validateMaxWeight('rates')) return;

    // Validate all boxes have items
    const emptyBox = boxes.find(b => !itemsByBox[b.id]?.length);
    if (emptyBox) {
      setRatesError('Remove empty boxes or assign items to them');
      return;
    }

    // Validate weights
    const invalidBox = boxes.find(b => (b.weightLb * 16 + b.weightOz) <= 0);
    if (invalidBox) {
      setRatesError('All boxes must have a weight');
      return;
    }
    
    setLoadingRates(true);
    setRatesError(null);
    
    try {
      const apiBoxes = boxes.map(box => ({
        id: `box-${box.id}`,
        items: (itemsByBox[box.id] || []).map(item => ({
          order_item_id: item.id.includes('-split-') ? item.id.split('-split-')[0] : item.id,
          quantity: item.quantity,
        })),
        weight: (box.weightLb * 16) + box.weightOz,
        length: box.length,
        width: box.width,
        height: box.height,
        ship_from_location_id: selectedLocationId || undefined,
      }));
      
      const insuranceAmt = insuranceOption !== 'none' ? insuranceAmount : undefined;
      const result = await shippingApi.getRates(order.id, apiBoxes, insuranceAmt, insuranceOption, signatureOption);
      
      setAvailableRates(result.rates || []);

      // Store method match from backend
      if (result.method_match) {
        setMethodMatch(result.method_match);
      }
      
      // Auto-select rate
      if (result.rates?.length > 0) {
        let autoSelected = null;
        
        // For free shipping, always pick cheapest
        if (result.method_match?.requires_free_shipping) {
          const sorted = [...result.rates].sort((a: ShippingRate, b: ShippingRate) => a.total_cost - b.total_cost);
          autoSelected = sorted[0];
        } else if (result.method_match?.suggested_rate_index !== null && result.method_match?.suggested_rate_index !== undefined) {
          autoSelected = result.rates[result.method_match.suggested_rate_index];
        } else {
          const sorted = [...result.rates].sort((a: ShippingRate, b: ShippingRate) => a.total_cost - b.total_cost);
          autoSelected = sorted[0];
        }
        
        setSelectedRate(autoSelected);
      }

      // Move to step 2
      setStep(2);
    } catch (error: any) {
      console.error('Failed to fetch rates:', error);
      setRatesError(error.response?.data?.error || 'Failed to get shipping rates');
    } finally {
      setLoadingRates(false);
    }
  }, [order?.id, boxes, itemsByBox, selectedLocationId, insuranceOption, insuranceAmount, signatureOption, validateMaxWeight]);

  // ============================================
  // Create Label
  // ============================================

  const createLabel = async () => {
    if (!selectedRate || !order?.id) {
      toast.error('Please select a shipping rate');
      return;
    }

    if (!validateMaxWeight('labels')) return;
    
    setCreatingLabel(true);
    
    try {
      const apiBoxes = boxes.map(box => ({
        id: `box-${box.id}`,
        items: (itemsByBox[box.id] || []).map(item => ({
          order_item_id: item.id.includes('-split-') ? item.id.split('-split-')[0] : item.id,
          quantity: item.quantity,
        })),
        weight: (box.weightLb * 16) + box.weightOz,
        length: box.length,
        width: box.width,
        height: box.height,
        ship_from_location_id: selectedLocationId || undefined,
      }));
      
      const result = await shippingApi.createLabels(order.id, apiBoxes, selectedRate, {
        labelFormat: 'pdf',
        insuranceAmount: insuranceOption !== 'none' ? insuranceAmount : 0,
        insuranceProvider: insuranceOption,
        signatureRequired: signatureOption,
        skipFulfillment,
      });
      
      // Store labels for review
      setCreatedLabels(result.labels || []);
      
      // Try to print labels (non-fatal - labels are already created)
      let printSuccess = true;
      for (const label of result.labels || []) {
        if (label.label_data) {
          try {
            await printerService.printLabel(label.label_data);
          } catch (printError: any) {
            console.warn('[Shipping] Print failed, opening in new tab:', printError.message);
            printSuccess = false;
            // Fallback: open PDF in new tab for manual printing
            try {
              const pdfData = label.label_data.replace(/^data:[^;]+;base64,/, '');
              const blob = new Blob([Uint8Array.from(atob(pdfData), c => c.charCodeAt(0))], { type: 'application/pdf' });
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
            } catch (fallbackError) {
              console.error('[Shipping] Fallback open failed:', fallbackError);
            }
          }
        }
      }
      
      setShowConfirmModal(false);
      
      if (printSuccess) {
        toast.success('Labels created and printed! Review and confirm to send tracking to Shopify.');
      } else {
        toast.success('Labels created! Opened in new tab for printing. Review and confirm to send tracking.');
      }
      
      // Show review modal instead of completing immediately
      setShowReviewModal(true);
    } catch (error: any) {
      console.error('Failed to create label:', error);
      const errorCode = error.response?.data?.code;
      const backendMsg = error.response?.data?.error;
      if (errorCode === 'BOX_OVER_MAX_WEIGHT') {
        const msg = backendMsg || 'A package exceeds the organization max package weight.';
        setMaxWeightError(msg);
        toast.error(msg);
      } else {
        toast.error(backendMsg || 'Failed to create label');
      }
    } finally {
      setCreatingLabel(false);
    }
  };

  const openCustomsDocsForLabels = (labels: any[]) => {
    const urls = Array.from(
      new Set(
        (labels || [])
          .map((label: any) => label?.customs_form_url)
          .filter((url: string | undefined) => !!url)
      )
    ) as string[];

    if (!urls.length) return;
    urls.forEach((url) => window.open(url, '_blank', 'noopener,noreferrer'));
  };

  const confirmShipment = async () => {
    try {
      // Confirm all shipments
      for (const label of createdLabels) {
        if (label.shipment_id) {
          await shippingApi.confirmShipment(label.shipment_id, skipFulfillment);
        }
      }
      
      setShowReviewModal(false);
      clearDraft();
      toast.success(skipFulfillment 
        ? 'Reship label created! (Shopify fulfillment not updated)' 
        : 'Shipment confirmed and tracking sent to Shopify!');
      onLabelCreated();
    } catch (error: any) {
      console.error('Failed to confirm shipment:', error);
      toast.error(error.response?.data?.error || 'Failed to confirm shipment');
    }
  };

  // ============================================
  // Render Step 1: Configure Packages
  // ============================================

  const renderStep1 = () => (
    <div className="flex h-full overflow-hidden">
      {/* Items Column */}
      <div className="flex-1 flex flex-col min-h-0 border-r border-slate-100">
        <div className="flex-shrink-0 p-4 pb-2">
          <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <CubeIcon className="w-4 h-4 text-slate-500" />
            Order Items
            <span className="text-xs font-normal text-slate-500">({items.length})</span>
          </h3>
          {boxes.length > 1 && (
            <p className="text-xs text-slate-500 mt-1">
              Use the <span className="inline-flex items-center px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">Box ▾</span> dropdown to assign items to different packages
            </p>
          )}
        </div>
        
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <div className="space-y-2">
            {items.map((item, idx) => {
              const itemWeight = parseFloat(String(
                item.resolved_weight ??
                item.variant_weight ??
                item.product_weight ??
                item.weight_oz ??
                0
              )) || 0;
              const displayWeight = itemWeight > 16 
                ? `${(itemWeight / 16).toFixed(1)} lb` 
                : `${itemWeight.toFixed(1)} oz`;
              const displayTitle = item.variant_title 
                ? `${item.title} - ${item.variant_title}`
                : item.title;
              const isSplit = item.id.includes('-split-');
              const originalItemId = getOriginalItemId(item.id);
              const firstSplitIndex = isSplit
                ? items.findIndex(i => getOriginalItemId(i.id) === originalItemId)
                : -1;
              const canClearSplit = isSplit && idx === firstSplitIndex;
              const boxIndex = boxes.findIndex(b => b.id === item.boxId);

              return (
                <div 
                  key={item.id} 
                  className={`rounded-lg px-3 py-2 border transition-colors ${
                    isSplit 
                      ? 'bg-amber-50 border-amber-300' 
                      : 'bg-white border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate" title={displayTitle}>
                        {displayTitle}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        {item.sku && <span className="font-mono">{item.sku}</span>}
                        {item.sku && <span>•</span>}
                        <span>{displayWeight} ea</span>
                        {isSplit && (
                          <span className="text-amber-600 font-medium">• Split</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Box Assignment - Made more prominent */}
                    <div className={`flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-lg border ${
                      boxIndex === 0 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'bg-emerald-50 border-emerald-200'
                    }`}>
                      <CubeIcon className={`w-3.5 h-3.5 flex-shrink-0 ${boxIndex === 0 ? 'text-blue-500' : 'text-emerald-500'}`} />
                      <select
                        value={item.boxId}
                        onChange={(e) => assignItemToBox(item.id, e.target.value)}
                        className={`bg-transparent border-0 text-xs font-semibold pl-0 pr-7 focus:ring-0 cursor-pointer ${
                          boxIndex === 0 ? 'text-blue-700' : 'text-emerald-700'
                        }`}
                      >
                        {boxes.map((box, idx) => (
                          <option key={box.id} value={box.id}>Box {idx + 1}</option>
                        ))}
                      </select>
                    </div>
                    
                    <span 
                      className="px-2 py-0.5 bg-slate-600 text-white text-xs font-medium rounded"
                      title="Quantity"
                    >
                      Qty: {item.quantity}
                    </span>
                    
                    {item.quantity > 1 && !isSplit && (
                      <>
                        <button
                          onClick={() => splitItemOnePerBox(item.id)}
                          className="px-1.5 py-1 text-[10px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded border border-indigo-200"
                          title="Split into one unit per box"
                        >
                          1/box
                        </button>
                        <button
                          onClick={() => setSplittingItem(item)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="Split across boxes"
                        >
                          <ArrowsRightLeftIcon className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                    {canClearSplit && (
                      <button
                        onClick={() => clearSplitGroup(item.id)}
                        className="px-1.5 py-1 text-[10px] font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 rounded border border-amber-200"
                        title="Merge split items back into one line"
                      >
                        Clear Split
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Boxes Column - Wider for better box display */}
      <div className="w-[360px] flex flex-col min-h-0 bg-slate-50">
        {/* Boxes Header */}
        <div className="flex-shrink-0 p-3 pb-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <CubeIcon className="w-4 h-4 text-slate-500" />
              Packages ({boxes.length})
            </h3>
            <button 
              onClick={addBox}
              className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-100 rounded border border-blue-200 font-medium flex items-center gap-1"
            >
              <PlusIcon className="w-3 h-3" /> Add
            </button>
          </div>
        </div>
        
        {/* Scrollable Boxes List */}
        <div className="flex-1 overflow-y-auto px-3">
          <div className="space-y-3 pb-2">
            {boxes.map((box, idx) => {
              const boxItems = itemsByBox[box.id] || [];
              const itemCount = boxItems.reduce((sum, i) => sum + i.quantity, 0);
              
              return (
                <div key={box.id} className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                      idx === 0 ? 'bg-blue-600 text-white' : 'bg-slate-500 text-white'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-800">Box {idx + 1}</p>
                      <p className="text-xs text-slate-500">{itemCount} item{itemCount !== 1 ? 's' : ''}</p>
                    </div>
                    {boxes.length > 1 && (
                      <button 
                        onClick={() => removeBox(box.id)} 
                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* Weight */}
                  <div className="mb-2">
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Weight</label>
                    <div className="flex items-center gap-1">
                      <div className="flex items-center gap-1 bg-slate-50 rounded px-2 py-1.5 border border-slate-200">
                        <ScaleIcon className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <input
                          type="number"
                          value={box.weightLb}
                          onChange={(e) => updateBox(box.id, { weightLb: Math.max(0, parseInt(e.target.value) || 0) })}
                          className="w-10 border-0 p-0 text-center text-sm font-semibold bg-transparent focus:ring-0"
                          min="0"
                        />
                        <span className="text-xs text-slate-500">lb</span>
                      </div>
                      <div className="flex items-center gap-1 bg-slate-50 rounded px-2 py-1.5 border border-slate-200">
                        <input
                          type="number"
                          value={box.weightOz}
                          onChange={(e) => updateBox(box.id, { weightOz: Math.max(0, Math.min(15, parseInt(e.target.value) || 0)) })}
                          className="w-8 border-0 p-0 text-center text-sm font-semibold bg-transparent focus:ring-0"
                          min="0"
                          max="15"
                        />
                        <span className="text-xs text-slate-500">oz</span>
                      </div>
                    </div>
                  </div>

                  {/* Dimensions */}
                  <div className="mb-2">
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Dimensions (L × W × H)</label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={box.length}
                        onChange={(e) => {
                          const val = e.target.value === '' ? '' : parseInt(e.target.value);
                          updateBox(box.id, { length: val as any, presetId: '' });
                        }}
                        onBlur={(e) => {
                          const val = parseInt(e.target.value) || 1;
                          updateBox(box.id, { length: Math.max(1, val), presetId: '' });
                        }}
                        className="w-14 px-1 py-1.5 border border-slate-200 rounded text-center text-sm font-semibold bg-slate-50"
                        min="1"
                      />
                      <span className="text-slate-400 text-xs">×</span>
                      <input
                        type="number"
                        value={box.width}
                        onChange={(e) => {
                          const val = e.target.value === '' ? '' : parseInt(e.target.value);
                          updateBox(box.id, { width: val as any, presetId: '' });
                        }}
                        onBlur={(e) => {
                          const val = parseInt(e.target.value) || 1;
                          updateBox(box.id, { width: Math.max(1, val), presetId: '' });
                        }}
                        className="w-14 px-1 py-1.5 border border-slate-200 rounded text-center text-sm font-semibold bg-slate-50"
                        min="1"
                      />
                      <span className="text-slate-400 text-xs">×</span>
                      <input
                        type="number"
                        value={box.height}
                        onChange={(e) => {
                          const val = e.target.value === '' ? '' : parseInt(e.target.value);
                          updateBox(box.id, { height: val as any, presetId: '' });
                        }}
                        onBlur={(e) => {
                          const val = parseInt(e.target.value) || 1;
                          updateBox(box.id, { height: Math.max(1, val), presetId: '' });
                        }}
                        className="w-14 px-1 py-1.5 border border-slate-200 rounded text-center text-sm font-semibold bg-slate-50"
                        min="1"
                      />
                      <span className="text-xs text-slate-500">in</span>
                    </div>
                  </div>

                  {/* Preset */}
                  <div>
                    <select
                      value={box.presetId}
                      onChange={(e) => {
                        const preset = packagePresets.find(p => p.id === e.target.value);
                        if (preset) {
                          applyPresetToBox(box.id, preset);
                        } else {
                          // Custom Size - no packaging weight
                          const newWeight = calculateBoxWeight(box.id, items, 0);
                          updateBox(box.id, { presetId: '', packagingWeight: 0, weightLb: newWeight.lb, weightOz: newWeight.oz });
                        }
                      }}
                      className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs bg-slate-50"
                    >
                      <option value="">Custom Size</option>
                      {[...packagePresets].sort((a, b) => a.length - b.length || a.width - b.width || a.height - b.height).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer - Always visible at bottom */}
        <div className="flex-shrink-0 border-t border-slate-200 bg-white p-3 mt-auto">
          {/* Options */}
          <div className="flex gap-2 mb-2">
            <select
              value={signatureOption}
              onChange={(e) => setSignatureOption(e.target.value as any)}
              className="flex-1 px-2 py-1.5 border border-slate-300 rounded text-xs bg-white"
            >
              <option value="none">No signature</option>
              <option value="signature">Signature</option>
              <option value="adult_signature">Adult 21+</option>
            </select>
            <select
              value={insuranceOption}
              onChange={(e) => {
                const newOption = e.target.value as 'none' | 'carrier' | 'parcelguard';
                setInsuranceOption(newOption);
                // Auto-fill with order total when selecting insurance, reset when deselecting
                if (newOption !== 'none' && insuranceAmount === 0) {
                  setInsuranceAmount(parseFloat(String(order.total_price)) || 0);
                } else if (newOption === 'none') {
                  setInsuranceAmount(0);
                }
              }}
              className="flex-1 px-2 py-1.5 border border-slate-300 rounded text-xs bg-white"
            >
              <option value="none">No insurance</option>
              <option value="carrier">Carrier</option>
              <option value="parcelguard">ParcelGuard</option>
            </select>
          </div>
          
          {insuranceOption !== 'none' && (
            <div className="mb-2">
              <input
                type="number"
                value={insuranceAmount}
                onChange={(e) => setInsuranceAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs"
                placeholder="Insurance amount $"
              />
            </div>
          )}

          {/* Location selector */}
          {locations.length > 1 && (
            <div className="mb-2">
              <select
                value={selectedLocationId}
                onChange={(e) => setSelectedLocationId(e.target.value)}
                className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs bg-white"
              >
                {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
              </select>
            </div>
          )}

          {/* Summary & Button */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Total:</span>
            <span className="text-sm font-bold text-slate-800">
              {totalWeight.lb} lb {totalWeight.oz} oz
              <span className="font-normal text-slate-500 ml-1">• {boxes.length} pkg</span>
            </span>
          </div>

          {maxPackageWeightLbs && (
            <div className="text-[11px] text-slate-500 mb-2">
              Org max package weight: {maxPackageWeightLbs} lb
            </div>
          )}

          {ratesError && (
            <div className="mb-2 p-2 bg-red-50 text-red-700 text-xs rounded flex items-center gap-1">
              <ExclamationTriangleIcon className="w-3 h-3" />
              {ratesError}
            </div>
          )}
          {!ratesError && maxWeightError && (
            <div className="mb-2 p-2 bg-red-50 text-red-700 text-xs rounded flex items-center gap-1">
              <ExclamationTriangleIcon className="w-3 h-3" />
              {maxWeightError}
            </div>
          )}

          <button
            onClick={fetchRates}
            disabled={loadingRates || !allItemsAssigned}
            className={`w-full py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 ${
              loadingRates || !allItemsAssigned
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {loadingRates ? (
              <><ArrowPathIcon className="w-4 h-4 animate-spin" /> Calculating...</>
            ) : (
              <>Calculate Rates →</>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // ============================================
  // Render Step 2: Select Rate & Ship
  // ============================================

  const renderStep2 = () => (
    <div className="flex flex-col h-full">
      {/* Main content */}
      <div className="flex-1 overflow-y-auto p-5">
        {/* Method Match Banner */}
        {methodMatch && (
          <div className={`rounded-xl p-4 mb-4 ${
            methodMatch.requires_free_shipping
              ? 'bg-green-50 border border-green-200'
              : methodMatch.confidence === 'exact'
                ? 'bg-blue-50 border border-blue-200'
                : 'bg-amber-50 border border-amber-200'
          }`}>
            <div className="flex items-center gap-3">
              {methodMatch.requires_free_shipping ? (
                <GiftIcon className="w-6 h-6 text-green-600" />
              ) : methodMatch.confidence === 'exact' ? (
                <CheckIcon className="w-6 h-6 text-blue-600" />
              ) : (
                <StarIcon className="w-6 h-6 text-amber-600" />
              )}
              <div>
                <p className="font-semibold text-slate-800">
                  Customer selected: {methodMatch.original_method || 'Standard Shipping'}
                </p>
                <p className="text-sm text-slate-600">
                  {methodMatch.requires_free_shipping
                    ? 'Free shipping — cheapest rate selected'
                    : methodMatch.message || 'Suggested rate highlighted below'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Rates List */}
        <h3 className="text-lg font-semibold text-slate-800 mb-4">
          Select Shipping Rate
          <span className="text-sm font-normal text-slate-500 ml-2">({sortedRates.length} available)</span>
        </h3>
        
        <div className="space-y-2">
          {sortedRates.map(rate => {
            const isCheapest = cheapestRate && rate.service_code === cheapestRate.service_code && rate.carrier_id === cheapestRate.carrier_id;
            const isSuggested = methodMatch?.requires_free_shipping
              ? isCheapest
              : suggestedRate && rate.service_code === suggestedRate.service_code && rate.carrier_id === suggestedRate.carrier_id;
            const isSelected = selectedRate?.service_code === rate.service_code && selectedRate?.carrier_id === rate.carrier_id;

            return (
              <button
                key={rate.rate_id}
                onClick={() => setSelectedRate(rate)}
                className={`w-full px-4 py-3 rounded-lg border-2 text-left transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 shadow-sm'
                    : isSuggested
                      ? 'border-green-300 bg-green-50 hover:border-green-400'
                      : 'border-slate-200 bg-white hover:border-blue-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <CarrierBadge carrier={rate.carrier} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-sm text-slate-900">
                        {rate.service_name || rate.service}
                      </span>
                      {isSuggested && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-green-100 text-green-700 rounded-full flex items-center gap-0.5">
                          {methodMatch?.requires_free_shipping ? (
                            <><CurrencyDollarIcon className="w-2.5 h-2.5" /> Cheapest</>
                          ) : (
                            <><StarIcon className="w-2.5 h-2.5" /> Suggested</>
                          )}
                        </span>
                      )}
                      {!isSuggested && isCheapest && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-green-100 text-green-700 rounded-full flex items-center gap-0.5">
                          <CurrencyDollarIcon className="w-2.5 h-2.5" /> Cheapest
                        </span>
                      )}
                      {rate.negotiated && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-purple-100 text-purple-700 rounded-full">
                          Negotiated
                        </span>
                      )}
                      {rate.is_third_party && (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-indigo-100 text-indigo-700 rounded-full">
                          Your Account
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      {rate.delivery_days ? `${rate.delivery_days} business days` : 'Delivery time varies'}
                      {rate.is_third_party && rate.third_party_account && (
                        <span className="ml-1 text-indigo-600">• {rate.third_party_account.nickname}</span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-slate-900">
                      ${(rate.total_cost || 0).toFixed(2)}
                    </span>
                    {isSelected && (
                      <CheckIcon className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="text-sm text-slate-500">Customer paid:</span>
            <span className="ml-1 font-bold text-slate-900">${(parseFloat(String(order.shipping_paid || 0)) || 0).toFixed(2)}</span>
          </div>
          <div>
            <span className="text-sm text-slate-500">{boxes.length} pkg</span>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setStep(1)}
            className="px-4 py-2.5 rounded-lg font-medium text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 flex items-center gap-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={() => setShowConfirmModal(true)}
            disabled={!selectedRate || creatingLabel}
            className={`flex-1 py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 ${
              selectedRate && !creatingLabel
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            <PrinterIcon className="w-4 h-4" />
            Create Label{boxes.length !== 1 ? 's' : ''}
            {selectedRate && <span>• ${(selectedRate.total_cost || 0).toFixed(2)}</span>}
          </button>
        </div>
        {/* Billing context note for 3rd-party carrier accounts */}
        {selectedRate?.is_third_party && (
          <p className="text-[11px] text-center mt-1.5 text-indigo-600">
            {insuranceOption === 'parcelguard'
              ? `Postage billed to your ${selectedRate.third_party_account?.nickname || 'carrier'} account. ParcelGuard deducted from wallet.`
              : insuranceOption === 'carrier'
                ? `Postage & insurance billed to your ${selectedRate.third_party_account?.nickname || 'carrier'} account.`
                : `Postage billed to your ${selectedRate.third_party_account?.nickname || 'carrier'} account — no wallet deduction.`
            }
          </p>
        )}
      </div>
    </div>
  );

  // ============================================
  // Main Render
  // ============================================
  
  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Step Indicator */}
      <div className="bg-blue-50 px-4 py-2 border-b border-blue-100 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm ${
              step === 1 ? 'bg-blue-600 text-white' : 'bg-green-500 text-white'
            }`}>
              {step > 1 ? <CheckIcon className="w-4 h-4" /> : '1'}
            </div>
            <span className={`text-sm font-semibold ${step === 1 ? 'text-blue-800' : 'text-green-700'}`}>
              Configure Packages
            </span>
          </div>
          <div className="flex-1 h-0.5 bg-blue-200"></div>
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm ${
              step === 2 ? 'bg-blue-600 text-white' : 'bg-slate-300 text-slate-600'
            }`}>
              2
            </div>
            <span className={`text-sm font-semibold ${step === 2 ? 'text-blue-800' : 'text-slate-500'}`}>
              Select Rate & Ship
            </span>
          </div>
        </div>
        {isFirstTimeCustomer && (
          <div className="mt-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-amber-100 text-amber-800 text-xs font-semibold border border-amber-200">
            <GiftIcon className="w-3.5 h-3.5" />
            First order customer - include welcome card
          </div>
        )}
      </div>

      {/* Content - fills remaining space */}
      <div className="flex-1 min-h-0">
        {step === 1 ? renderStep1() : renderStep2()}
      </div>

      {/* Split Modal */}
      {splittingItem && (
        <SplitModal
          item={splittingItem}
          boxes={boxes}
          onSplit={(splits, newBoxQty) => handleSplit(splittingItem.id, splits, newBoxQty)}
          onCancel={() => setSplittingItem(null)}
        />
      )}

      {/* Confirm Modal */}
      {showConfirmModal && selectedRate && (
        <ConfirmModal
          rate={selectedRate}
          boxCount={boxes.length}
          onConfirm={createLabel}
          onCancel={() => setShowConfirmModal(false)}
          isLoading={creatingLabel}
        />
      )}

      {/* Review Labels Modal */}
      {showReviewModal && createdLabels.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-[500px] max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Review Shipping Labels</h3>
            
            <div className="space-y-3 mb-6">
              {createdLabels.map((label, index) => (
                <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-600">Package {index + 1}</span>
                    <span className="text-xs text-slate-500">{label.carrier?.toUpperCase()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TruckIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-mono font-semibold text-slate-900 truncate">
                        {label.tracking_number}
                      </p>
                      {label.tracking_url && (
                        <a 
                          href={label.tracking_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          Track Package <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                  {label.label_data && (
                    <button
                      onClick={() => printerService.printLabel(label.label_data)}
                      className="mt-2 w-full px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-50 flex items-center justify-center gap-1"
                    >
                      <PrinterIcon className="w-3 h-3" /> Reprint Label
                    </button>
                  )}
                  {label.customs_form_url && (
                    <a
                      href={label.customs_form_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 w-full px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded hover:bg-amber-100 flex items-center justify-center gap-1"
                    >
                      <ArrowTopRightOnSquareIcon className="w-3 h-3" /> Open Commercial Invoice
                    </a>
                  )}
                </div>
              ))}
            </div>

            {createdLabels.some((label: any) => !!label.customs_form_url) && (
              <button
                onClick={() => openCustomsDocsForLabels(createdLabels)}
                className="w-full mb-4 px-3 py-2 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded hover:bg-amber-100 flex items-center justify-center gap-1"
              >
                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" /> Open All Commercial Invoices
              </button>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Labels have been created and printed. Click "Confirm & Send Tracking" to mark the order as shipped and send tracking information to Shopify.
              </p>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={() => {
                  const confirmed = window.confirm(
                    'Labels have been created but tracking has NOT been sent to Shopify yet.\n\nAre you sure you want to cancel without confirming?'
                  );
                  if (!confirmed) return;
                  setShowReviewModal(false);
                  setCreatedLabels([]);
                }}
                className="flex-1 px-4 py-2.5 text-sm font-medium border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmShipment}
                className="flex-1 px-4 py-2.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
              >
                <CheckCircleIcon className="w-4 h-4" /> Confirm & Send Tracking
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedShippingView;
